import { useEffect, useRef, useState } from 'react';

// Estimate total transcription time in seconds based on file size.
// Heuristic: typical MP3 ~50 KB/sec; Sarvam runs ~5x realtime + ~30s overhead.
export function estimateTranscriptionTimeSeconds(fileSizeBytes) {
  if (!fileSizeBytes || fileSizeBytes < 0) return 60;
  const estimatedDurationSeconds = Math.max(1, Math.floor(fileSizeBytes / (50 * 1024)));
  return estimatedDurationSeconds * 5 + 30;
}

// Polling timeout for a recording of known length: 10 min floor, 1.5× audio
// duration, capped at 60 min — mirrors the backend's duration-scaled batch
// polling window so the UI never declares timeout while the backend is still
// legitimately working. Unknown duration (0/null) → the 10-minute default.
export function transcriptionTimeoutMs(durationSeconds) {
  const TEN_MIN = 10 * 60 * 1000;
  const SIXTY_MIN = 60 * 60 * 1000;
  if (!durationSeconds || durationSeconds <= 0) return TEN_MIN;
  return Math.min(SIXTY_MIN, Math.max(TEN_MIN, durationSeconds * 1.5 * 1000));
}

/**
 * Drive a poll loop for an async transcription job and return progress state for a banner.
 *
 * Two backend patterns are supported via the caller-supplied `pollFn` + `isComplete`/`isFailed`:
 *   • Pending-sessions table: pollFn fetches GET /sessions/:id
 *   • Speech-row polling:    pollFn fetches GET /speeches/:id
 *
 * Polling stops automatically when isComplete/isFailed returns true, on timeout, or when
 * the hook is disabled or unmounts.
 *
 * @param {object}   opts
 * @param {boolean}  opts.enabled            Start/stop polling.
 * @param {function} opts.pollFn             async () => result.
 * @param {function} opts.isComplete         (result) => boolean.
 * @param {function} [opts.isFailed]         (result) => boolean. Defaults to false.
 * @param {function} [opts.getError]         (result) => string|null. Used when isFailed → true.
 * @param {function} [opts.onComplete]       (result) => void. Fired once when polling resolves.
 * @param {function} [opts.onFail]           (errorMsg) => void. Fired once on failure or timeout.
 * @param {number}   [opts.intervalMs=2000]
 * @param {number}   [opts.timeoutMs=600000] 10 minutes by default.
 * @param {number}   [opts.estimatedFileSize] Drives progressPercent. 0 → indeterminate.
 *
 * @returns {{ elapsedSeconds: number, progressPercent: number, errorMsg: string|null, timedOut: boolean }}
 */
export default function useBackgroundTranscription({
  enabled,
  pollFn,
  isComplete,
  isFailed = () => false,
  getError = () => null,
  onComplete,
  onFail,
  intervalMs = 2000,
  timeoutMs = 10 * 60 * 1000,
  estimatedFileSize = 0,
}) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [timedOut, setTimedOut] = useState(false);

  // Refs let the polling loop read the freshest callbacks without re-subscribing on every render.
  const pollFnRef = useRef(pollFn);
  const isCompleteRef = useRef(isComplete);
  const isFailedRef = useRef(isFailed);
  const getErrorRef = useRef(getError);
  const onCompleteRef = useRef(onComplete);
  const onFailRef = useRef(onFail);

  useEffect(() => { pollFnRef.current = pollFn; }, [pollFn]);
  useEffect(() => { isCompleteRef.current = isComplete; }, [isComplete]);
  useEffect(() => { isFailedRef.current = isFailed; }, [isFailed]);
  useEffect(() => { getErrorRef.current = getError; }, [getError]);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onFailRef.current = onFail; }, [onFail]);

  useEffect(() => {
    if (!enabled) {
      setElapsedSeconds(0);
      setErrorMsg(null);
      setTimedOut(false);
      return undefined;
    }

    let cancelled = false;

    const fail = (msg) => {
      if (cancelled) return;
      setErrorMsg(msg);
      onFailRef.current?.(msg);
    };

    const tick = async () => {
      try {
        const result = await pollFnRef.current();
        if (cancelled) return;
        if (isFailedRef.current(result)) {
          fail(getErrorRef.current(result) || 'Transcription failed. Please try again.');
          clearInterval(pollHandle);
          clearTimeout(timeoutHandle);
          clearInterval(timerHandle);
          return;
        }
        if (isCompleteRef.current(result)) {
          clearInterval(pollHandle);
          clearTimeout(timeoutHandle);
          clearInterval(timerHandle);
          onCompleteRef.current?.(result);
        }
      } catch (err) {
        // Swallow transient errors — keep polling. Network blips, 404 races, etc.
        // The timeout will fire if we never recover.
        console.warn('[useBackgroundTranscription] poll error:', err.message);
      }
    };

    const pollHandle = setInterval(tick, intervalMs);
    const timeoutHandle = setTimeout(() => {
      if (cancelled) return;
      setTimedOut(true);
      fail(`Processing timed out after ${Math.round(timeoutMs / 60000)} minutes. Please try a shorter file.`);
      clearInterval(pollHandle);
      clearInterval(timerHandle);
    }, timeoutMs);
    const timerHandle = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    // Fire one poll immediately so we don't wait `intervalMs` before the first check.
    tick();

    return () => {
      cancelled = true;
      clearInterval(pollHandle);
      clearTimeout(timeoutHandle);
      clearInterval(timerHandle);
    };
  }, [enabled, intervalMs, timeoutMs]);

  const estimatedTotalSeconds = estimateTranscriptionTimeSeconds(estimatedFileSize);
  const progressPercent = estimatedFileSize > 0
    ? Math.min(95, Math.round((elapsedSeconds / estimatedTotalSeconds) * 100))
    : 0;

  return { elapsedSeconds, progressPercent, errorMsg, timedOut };
}
